import React, { useState, useEffect } from "react";
import { getDonations, createDonation, updateDonation, deleteDonation, uploadReportPdf } from "../../../APIClient/apiClient";
import Toast from "../../../component/Toast";
import "../../../style/admin/DonationManager.css";

const DonationManager = () => {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [formData, setFormData] = useState({
    file_url: "",
    en: { file_name: "", compilation_date_text: "", release_date_text: "" },
    vi: { file_name: "", compilation_date_text: "", release_date_text: "" },
    is_active: true,
    sort_order: 0,
  });

  useEffect(() => {
    fetchDonations();
  }, []);

  const fetchDonations = async () => {
    setLoading(true);
    try {
      const data = await getDonations();
      setDonations(data);
    } catch (err) {
      setToast({ message: "Failed to load donations", type: "error" });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (language, field, value) => {
    setFormData({
      ...formData,
      [language]: {
        ...formData[language],
        [field]: value,
      },
    });
  };

  const handleMetaChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handlePdfFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isPdfMime = file.type === "application/pdf" || file.type === "application/x-pdf";
    const hasPdfExtension = file.name.toLowerCase().endsWith(".pdf");

    if (!isPdfMime && !hasPdfExtension) {
      setToast({ message: "Please select a PDF file", type: "error" });
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setToast({ message: "PDF size must be less than 20MB", type: "error" });
      return;
    }

    setUploadingPdf(true);
    try {
      const result = await uploadReportPdf(file);
      setFormData((prev) => ({
        ...prev,
        file_url: result.fileUrl,
      }));
      setToast({ message: "PDF uploaded successfully", type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to upload PDF", type: "error" });
    } finally {
      setUploadingPdf(false);
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateDonation(editingId, formData);
      } else {
        await createDonation(formData);
      }
      fetchDonations();
      resetForm();
      setToast({ message: "Donation project saved successfully", type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to save donation", type: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this donation project?")) {
      try {
        await deleteDonation(id);
        fetchDonations();
        setToast({ message: "Donation project deleted successfully", type: "success" });
      } catch (err) {
        setToast({ message: "Failed to delete donation", type: "error" });
      }
    }
  };

  const handleEdit = (donation) => {
    const enTranslation = donation.translations.find((t) => t.language === "en") || {
      file_name: "",
      compilation_date_text: "",
      release_date_text: "",
    };
    const viTranslation = donation.translations.find((t) => t.language === "vi") || {
      file_name: "",
      compilation_date_text: "",
      release_date_text: "",
    };

    setFormData({
      file_url: donation.file_url || "",
      en: { ...enTranslation },
      vi: { ...viTranslation },
      is_active: donation.is_active,
      sort_order: donation.sort_order,
    });
    setEditingId(donation.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      file_url: "",
      en: { file_name: "", compilation_date_text: "", release_date_text: "" },
      vi: { file_name: "", compilation_date_text: "", release_date_text: "" },
      is_active: true,
      sort_order: 0,
    });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return <div className="admin-manager-loading">Loading donations...</div>;

  return (
    <div className="admin-manager">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="admin-manager-header">
        <button className="admin-manager-btn-primary" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
          {showForm ? "Cancel" : "Add New Donation Project"}
        </button>
      </div>

      {showForm && (
        <div className="admin-manager-form">
          <h3>{editingId ? "Edit Donation" : "Create New Donation"}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Upload PDF</label>
                <input type="file" accept="application/pdf,.pdf" onChange={handlePdfFileChange} disabled={uploadingPdf} />
                {uploadingPdf && <p style={{ color: "blue", marginTop: "5px" }}>Uploading PDF...</p>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>File URL (auto after upload)</label>
                <input
                  type="text"
                  value={formData.file_url}
                  onChange={(e) => handleMetaChange("file_url", e.target.value)}
                  placeholder="/Reports/your-file.pdf"
                />
              </div>
            </div>

            <div className="form-section">
              <h4>English</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>File Name</label>
                  <input
                    type="text"
                    value={formData.en.file_name}
                    onChange={(e) => handleInputChange("en", "file_name", e.target.value)}
                    placeholder="Donation report file name"
                  />
                </div>
                <div className="form-group">
                  <label>Compilation Date</label>
                  <input
                    type="text"
                    value={formData.en.compilation_date_text}
                    onChange={(e) => handleInputChange("en", "compilation_date_text", e.target.value)}
                    placeholder="Compilation date"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Release Date Text</label>
                  <input
                    type="text"
                    value={formData.en.release_date_text}
                    onChange={(e) => handleInputChange("en", "release_date_text", e.target.value)}
                    placeholder="Release date"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h4>Vietnamese</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Tên tệp</label>
                  <input
                    type="text"
                    value={formData.vi.file_name}
                    onChange={(e) => handleInputChange("vi", "file_name", e.target.value)}
                    placeholder="Tên tệp"
                  />
                </div>
                <div className="form-group">
                  <label>Ngày tổng hợp</label>
                  <input
                    type="text"
                    value={formData.vi.compilation_date_text}
                    onChange={(e) => handleInputChange("vi", "compilation_date_text", e.target.value)}
                    placeholder="Ngày tổng hợp"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Ngày phát hành</label>
                  <input
                    type="text"
                    value={formData.vi.release_date_text}
                    onChange={(e) => handleInputChange("vi", "release_date_text", e.target.value)}
                    placeholder="Ngày phát hành"
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Sort Order</label>
                <input type="number" value={formData.sort_order} onChange={(e) => handleMetaChange("sort_order", parseInt(e.target.value))} />
              </div>
              <div className="form-group">
                <label>Active</label>
                <input type="checkbox" checked={formData.is_active} onChange={(e) => handleMetaChange("is_active", e.target.checked)} />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? "Update" : "Create"} Donation
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-manager-table">
        <table>
          <thead>
            <tr>
              <th>No.</th>
              <th>File name (EN)</th>
              <th>Compilation Date (EN)</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {donations.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>
                  No donation projects found
                </td>
              </tr>
            ) : (
              donations.map((donation, index) => (
                <tr key={donation.id}>
                  <td>{index + 1}</td>
                  <td>{donation.translations.find((t) => t.language === "en")?.file_name || "-"}</td>
                  <td>{donation.translations.find((t) => t.language === "en")?.compilation_date_text || "-"}</td>
                  <td>{donation.is_active ? "Yes" : "No"}</td>
                  <td className="action-buttons">
                    <button className="btn-edit" onClick={() => handleEdit(donation)}>
                      Edit
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(donation.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DonationManager;
